import type { RouteObject } from "react-router";
import { Navigate, Outlet, replace, useParams } from "react-router";

import { UserClient } from "@citrine/resources/user";

import { FeatureFlag } from "#src/components/feature-flag";
import { RootContext } from "#src/contexts/root";

import { Component as Index } from "./_index/route";
import { Component as Layout } from "./_layout/route";
import { projectRedirects } from "./projects.$projectId/redirects";
import { Component as ProjectLayout } from "./projects.$projectId/route";

import "#src/styles/reactflow";

export const routes = [
  {
    path: "/*",
    element: (
      <RootContext>
        <Layout />
      </RootContext>
    ),
    children: [
      {
        index: true,
        Component: Index,
      },
      {
        path: "account",
        children: [
          {
            index: true,
            path: "*",
            lazy: () => import("./account/route"),
          },
        ],
      },
      {
        path: "admin",
        Component: () =>
          UserClient.getInstance().useGetMe().data?.is_admin ? (
            <Outlet />
          ) : (
            <Navigate to="/" replace />
          ),
        children: [
          {
            index: true,
            path: "*",
            lazy: () => import("./admin/route"),
          },
        ],
      },
      {
        path: "data-manager",
        children: [
          {
            path: "teams/:teamId/ingestions",
            Component: () => {
              const { teamId } = useParams<{ teamId: string }>();

              return (
                <FeatureFlag
                  feature="enableMaterialsV3"
                  fallback={<Navigate to={`/data-manager/teams/${teamId}`} />}
                >
                  <Outlet />
                </FeatureFlag>
              );
            },
            children: [
              {
                index: true,
                lazy: () => import("./data-manager.$teamId.ingestions/route"),
              },
              {
                path: "new",
                lazy: () =>
                  import("./data-manager.$teamId.ingestions.new/route"),
              },
              {
                path: ":ingestionId",
                lazy: () =>
                  import("./data-manager.$teamId.ingestions.$ingestionId/route"),
              },
            ],
          },
          {
            index: true,
            path: "*",
            lazy: () => import("./data-manager/route"),
          },
        ],
      },
      {
        path: "projects",
        children: [
          {
            index: true,
            lazy: () => import("./projects._index/route"),
          },
          {
            path: ":projectId",
            Component: ProjectLayout,
            children: [
              ...projectRedirects,
              {
                index: true,
                lazy: () => import("./projects.$projectId._index/route"),
              },
              {
                path: "branches/*",
                lazy: () => import("./projects.$projectId.branches/route"),
              },
              {
                path: "data/*",
                lazy: () => import("./projects.$projectId.data/route"),
              },
              {
                path: "predictors/*",
                lazy: () => import("./projects.$projectId.predictors/route"),
              },
              {
                path: "search-spaces/*",
                lazy: () => import("./projects.$projectId.search-spaces/route"),
              },
              {
                path: "workspaces/*",
                lazy: () => import("./projects.$projectId.workspaces/route"),
              },
              {
                path: "*",
                loader: ({ params }) =>
                  replace(`/projects/${params.projectId}`),
              },
            ],
          },
        ],
      },
      {
        path: "*",
        loader: () => replace("/"),
      },
    ],
  },
] as const satisfies RouteObject[];
